import { useEffect, useState, type HTMLAttributes, type ReactNode } from 'react';
import { AlertDialog as AlertPrimitive, Dialog as DialogPrimitive } from 'radix-ui';
import { CircleAlert, X } from 'lucide-react';
import { cn } from '../../lib/cn';
import { useControlled } from '../../lib/use-controlled';
import { Callout } from '../elbklar/callout';
import { Button } from './button';
import { IconButton } from './button';

const overlay = 'fixed inset-0 z-[2000] bg-background/70 animate-ek-in';
const flaeche =
  'fixed left-1/2 top-1/2 z-[2000] flex max-h-[calc(100dvh-2rem)] w-[calc(100%-2rem)] -translate-x-1/2 -translate-y-1/2 flex-col gap-4 overflow-y-auto rounded-xl border border-border bg-popover p-6 text-popover-foreground shadow-md animate-ek-pop focus-visible:outline-none';

export const Dialog = DialogPrimitive.Root;
export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogClose = DialogPrimitive.Close;

export interface DialogContentProps extends DialogPrimitive.DialogContentProps {
  /** Breite: `sm` 400 px, `md` 512 px (Standard), `lg` 720 px. */
  size?: 'sm' | 'md' | 'lg';
  /** Schließen-Knopf oben rechts ausblenden. */
  hideClose?: boolean;
}

/** Ein Dialog: radius-xl auf popover, Schatten nur hier, weil er schwebt. */
export function DialogContent({ size = 'md', hideClose, className, children, ...rest }: DialogContentProps) {
  return (
    <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay className={overlay} />
      <DialogPrimitive.Content
        className={cn(flaeche, size === 'sm' ? 'max-w-[400px]' : size === 'lg' ? 'max-w-[720px]' : 'max-w-lg', className)}
        {...rest}
      >
        {children}
        {!hideClose && (
          <DialogPrimitive.Close asChild>
            <IconButton icon={X} label="Schließen" variant="ghost" size="sm" noTooltip className="absolute right-4 top-4" />
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  );
}

export function DialogHeader({ className, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('flex flex-col gap-1 pr-8', className)} {...rest} />;
}
export function DialogFooter({ className, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('flex flex-col-reverse gap-2 sm:flex-row sm:justify-end', className)} {...rest} />;
}
export function DialogTitle({ className, ...rest }: DialogPrimitive.DialogTitleProps) {
  return <DialogPrimitive.Title className={cn('t-title', className)} {...rest} />;
}
export function DialogDescription({ className, ...rest }: DialogPrimitive.DialogDescriptionProps) {
  return <DialogPrimitive.Description className={cn('t-body text-muted-foreground', className)} {...rest} />;
}

interface ConfirmDialogBasis {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Auslöser, falls der Dialog nicht von außen geöffnet wird. */
  trigger?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  cancelLabel?: string;
  /** Läuft außerhalb, z. B. eine Mutation: Knopf im Ladezustand. */
  busy?: boolean;
}

/**
 * Entweder eine Bestätigung (`confirmLabel` **und** `onConfirm`) oder ein
 * Hinweis, der nur schließt (beides weggelassen), etwa wenn etwas nicht geht
 * und es nichts zu bestätigen gibt. Nur eins von beidem ist ein Fehler beim
 * Übersetzen, damit eine Aktion nicht still unerreichbar wird.
 */
export type ConfirmDialogProps = ConfirmDialogBasis &
  (
    | {
        /** Verb zuerst: „Kunde löschen“. */
        confirmLabel: string;
        /** `danger` für Löschen und Unumkehrbares. */
        tone?: 'primary' | 'danger';
        /**
         * Gibt sie ein Promise zurück, bleibt der Dialog offen, bis es erfüllt ist:
         * Ladezustand am Knopf, bei Fehler eine Meldung im Dialog statt still zu
         * schließen. Ohne Promise schließt er sofort.
         */
        onConfirm: (() => void) | (() => Promise<unknown>);
      }
    | { confirmLabel?: never; tone?: never; onConfirm?: never }
  );

/** Text aus einer FastAPI-Antwort (`detail`), wie ihn CRM und PM über axios bekommen. */
function serverMeldung(fehler: unknown): string | undefined {
  const detail = (fehler as { response?: { data?: { detail?: unknown } } } | null)?.response?.data?.detail;
  return typeof detail === 'string' && detail.trim() ? detail : undefined;
}

/** Bestätigung im App-Stil. Ersetzt window.confirm, das es in Elbklar-Apps nicht gibt. */
export function ConfirmDialog({
  open,
  onOpenChange,
  trigger,
  title,
  description,
  confirmLabel,
  cancelLabel,
  tone = 'danger',
  onConfirm,
  busy,
}: ConfirmDialogProps) {
  const [offen, setOffen] = useControlled(open, false, onOpenChange);
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<{ text?: string } | null>(null);

  useEffect(() => {
    if (offen) setFehler(null);
  }, [offen]);

  const nurHinweis = onConfirm === undefined;

  const bestaetigen = async () => {
    if (!onConfirm) return;
    setFehler(null);
    let ergebnis: unknown;
    try {
      ergebnis = onConfirm();
    } catch (e) {
      setFehler({ text: serverMeldung(e) });
      return;
    }
    if (!ergebnis || typeof (ergebnis as PromiseLike<unknown>).then !== 'function') {
      setOffen(false);
      return;
    }
    setLaeuft(true);
    try {
      await ergebnis;
      setLaeuft(false);
      setOffen(false);
    } catch (e) {
      setLaeuft(false);
      setFehler({ text: serverMeldung(e) });
    }
  };

  return (
    <AlertPrimitive.Root open={offen} onOpenChange={(o) => !laeuft && setOffen(o)}>
      {trigger && <AlertPrimitive.Trigger asChild>{trigger}</AlertPrimitive.Trigger>}
      <AlertPrimitive.Portal>
        <AlertPrimitive.Overlay className={overlay} />
        <AlertPrimitive.Content className={cn(flaeche, 'max-w-[400px]')} onEscapeKeyDown={(e) => laeuft && e.preventDefault()}>
          <div className="flex flex-col gap-1">
            <AlertPrimitive.Title className="t-title">{title}</AlertPrimitive.Title>
            {description && <AlertPrimitive.Description className="t-body text-muted-foreground">{description}</AlertPrimitive.Description>}
          </div>
          {fehler && (
            <Callout tone="danger" icon={CircleAlert} title="Fehlgeschlagen" boxed>
              {fehler.text ?? 'Erneut versuchen oder abbrechen.'}
            </Callout>
          )}
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <AlertPrimitive.Cancel asChild>
              <Button variant={nurHinweis ? 'secondary' : 'ghost'} disabled={laeuft}>
                {cancelLabel ?? (nurHinweis ? 'Schließen' : 'Abbrechen')}
              </Button>
            </AlertPrimitive.Cancel>
            {!nurHinweis && (
              <Button variant={tone} onClick={bestaetigen} loading={laeuft || busy}>
                {confirmLabel}
              </Button>
            )}
          </div>
        </AlertPrimitive.Content>
      </AlertPrimitive.Portal>
    </AlertPrimitive.Root>
  );
}
